import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/store/ProductCard";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SIZES = ["XS", "S", "M", "L", "XL"];
const COLORS = ["Blush Pink", "Dusty Rose", "Warm Ivory", "Soft Mauve"];
const SORTS = [["featured", "Featured"], ["newest", "Newest"], ["price-asc", "Price: Low → High"], ["price-desc", "Price: High → Low"], ["best-selling", "Best Selling"]];

export default function Shop() {
  const { category } = useParams();
  const { categories } = useStore();
  const [sp] = useSearchParams();
  const search = sp.get("search") || "";
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [maxPrice, setMaxPrice] = useState("");
  const [flags, setFlags] = useState({ is_new: false, best_sellers: false, return_eligible: false, in_stock: false });
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const catName = category ? (categories.find((c) => c.slug === category)?.name || category.replace(/-/g, " ")) : (search ? `“${search}”` : "All Products");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (search) params.set("search", search);
    params.set("sort", sort);
    if (sizes.length) params.set("sizes", sizes.join(","));
    if (colors.length) params.set("colors", colors.join(","));
    if (maxPrice) params.set("max_price", maxPrice);
    Object.entries(flags).forEach(([k, v]) => v && params.set(k, "true"));
    try { const r = await api.get(`/products?${params.toString()}`); setProducts(r.data); } catch {}
    setLoading(false);
  }, [category, search, sort, sizes, colors, maxPrice, flags]);

  useEffect(() => { load(); }, [load]);

  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  const clearAll = () => { setSizes([]); setColors([]); setMaxPrice(""); setFlags({ is_new: false, best_sellers: false, return_eligible: false, in_stock: false }); };

  const Filters = () => (
    <div className="space-y-7">
      <div>
        <h4 className="text-xs uppercase tracking-widest font-semibold mb-3">Size</h4>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} data-testid={`filter-size-${s}`} onClick={() => toggle(sizes, setSizes, s)}
              className={`w-10 h-10 text-sm border ${sizes.includes(s) ? "bg-sharoa-charcoal text-white border-sharoa-charcoal" : "border-sharoa-line text-sharoa-charcoal"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-widest font-semibold mb-3">Colour</h4>
        <div className="flex flex-col gap-2">
          {COLORS.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={colors.includes(c)} onChange={() => toggle(colors, setColors, c)} className="accent-sharoa-rose" />{c}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-widest font-semibold mb-3">Max Price</h4>
        <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="₹ any"
          className="w-full border border-sharoa-line px-3 py-2 text-sm outline-none focus:border-sharoa-rose" />
      </div>
      <div className="space-y-2">
        {[["is_new", "New Arrivals"], ["best_sellers", "Best Sellers"], ["return_eligible", "Return Eligible"], ["in_stock", "In Stock"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={flags[k]} onChange={() => setFlags((f) => ({ ...f, [k]: !f[k] }))} className="accent-sharoa-rose" />{l}
          </label>
        ))}
      </div>
      <button onClick={clearAll} className="text-xs uppercase tracking-widest text-sharoa-rose sh-underline">Clear All</button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-sharoa-muted">SHAROA</p>
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tight capitalize mt-1">{catName}</h1>
        <p className="text-sm text-sharoa-muted mt-2">{products.length} {products.length === 1 ? "style" : "styles"}</p>
      </div>

      <div className="flex gap-10">
        <aside className="hidden lg:block w-56 shrink-0"><Filters /></aside>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-filter-btn" className="lg:hidden flex items-center gap-2 border border-sharoa-line px-4 py-2.5 text-xs uppercase tracking-widest">
                  <SlidersHorizontal size={15} /> Filter & Sort
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-white overflow-y-auto w-[85vw]">
                <div className="flex items-center justify-between mb-6"><h3 className="font-serif text-xl">Filters</h3><button onClick={() => setFilterOpen(false)}><X size={18} /></button></div>
                <Filters />
              </SheetContent>
            </Sheet>
            <div className="ml-auto">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-48 border-sharoa-line text-xs uppercase tracking-widest"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-sharoa-ivory animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center text-sharoa-muted">No styles found. Try adjusting your filters.</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
